<script setup lang="ts">
import { Crop, Trash2 } from 'lucide-vue-next'
import { VisuallyHidden } from 'reka-ui'
import { Dialog, DialogContent, DialogClose, DialogDescription, DialogFooter, DialogTitle } from '@/ui/dialog'

const { t } = useI18n()

const props = defineProps<{
  src: string | null
  aspectRatio?: number | [number, number]
  ratioName?: string
}>()

const numericRatio = computed<number | undefined>(() => {
  const ar = props.aspectRatio
  if (ar === undefined) return undefined
  return Array.isArray(ar) ? ar[0] / ar[1] : ar
})

const ratioLabel = computed<string | null>(() => {
  const ar = props.aspectRatio
  if (ar === undefined) return null
  return Array.isArray(ar) ? `${ar[0]}:${ar[1]}` : `${ar}:1`
})

const formatLabel = computed(() => {
  if (!ratioLabel.value) return t('uploader.crop.freeRatio')
  if (props.ratioName) return t('uploader.crop.namedRatio', { ratio: ratioLabel.value, name: props.ratioName })
  return t('uploader.crop.fixedRatio', { ratio: ratioLabel.value })
})

const emit = defineEmits<{
  confirm: [blob: Blob]
  cancel: []
}>()

const open = computed({
  get: () => !!props.src,
  set: (val: boolean) => {
    if (!val) emit('cancel')
  }
})

const imgRef = ref<HTMLImageElement | null>(null)
const containerRef = ref<HTMLDivElement | null>(null)
const isDragging = ref(false)

const cropArea = ref({ x: 0, y: 0, width: 0, height: 0 })
const dragStart = ref({ x: 0, y: 0, cropX: 0, cropY: 0 })
const resizing = ref(false)

const MIN_WIDTH = 40

function onImageLoad() {
  if (!imgRef.value || !containerRef.value) return
  const img = imgRef.value
  const containerWidth = containerRef.value.clientWidth
  const containerHeight = containerRef.value.clientHeight

  const displayWidth = img.clientWidth
  const displayHeight = img.clientHeight

  const offsetX = (containerWidth - displayWidth) / 2
  const offsetY = (containerHeight - displayHeight) / 2

  const ar = numericRatio.value
  let w: number
  let h: number
  if (ar) {
    w = displayWidth * 0.7
    h = w / ar
    if (h > displayHeight * 0.7) {
      h = displayHeight * 0.7
      w = h * ar
    }
  } else {
    w = displayWidth
    h = displayHeight
  }

  cropArea.value = {
    x: offsetX + (displayWidth - w) / 2,
    y: offsetY + (displayHeight - h) / 2,
    width: w,
    height: h
  }
}

function getImageBounds() {
  if (!imgRef.value || !containerRef.value) return { x: 0, y: 0, w: 0, h: 0 }
  const cw = containerRef.value.clientWidth
  const ch = containerRef.value.clientHeight
  const dw = imgRef.value.clientWidth
  const dh = imgRef.value.clientHeight
  return {
    x: (cw - dw) / 2,
    y: (ch - dh) / 2,
    w: dw,
    h: dh
  }
}

function clampCrop() {
  const bounds = getImageBounds()
  const c = cropArea.value
  const ar = numericRatio.value

  if (ar) {
    if (c.width > bounds.w) {
      c.width = bounds.w
      c.height = c.width / ar
    }
    if (c.height > bounds.h) {
      c.height = bounds.h
      c.width = c.height * ar
    }
    if (c.width < MIN_WIDTH) {
      c.width = MIN_WIDTH
      c.height = c.width / ar
    }
  } else {
    c.width = Math.max(MIN_WIDTH, Math.min(c.width, bounds.w))
    c.height = Math.max(MIN_WIDTH, Math.min(c.height, bounds.h))
  }

  c.x = Math.max(bounds.x, Math.min(c.x, bounds.x + bounds.w - c.width))
  c.y = Math.max(bounds.y, Math.min(c.y, bounds.y + bounds.h - c.height))
}

function onMouseDown(e: MouseEvent, isResize = false) {
  e.preventDefault()
  isDragging.value = true
  resizing.value = isResize
  dragStart.value = {
    x: e.clientX,
    y: e.clientY,
    cropX: cropArea.value.x,
    cropY: cropArea.value.y
  }
  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

function onMouseMove(e: MouseEvent) {
  if (!isDragging.value) return
  const dx = e.clientX - dragStart.value.x
  const dy = e.clientY - dragStart.value.y

  if (resizing.value) {
    const ar = numericRatio.value
    if (ar) {
      const delta = (dx + dy) / 2
      cropArea.value.width = cropArea.value.width + delta
      cropArea.value.height = cropArea.value.width / ar
    } else {
      cropArea.value.width = cropArea.value.width + dx
      cropArea.value.height = cropArea.value.height + dy
    }
    dragStart.value.x = e.clientX
    dragStart.value.y = e.clientY
  } else {
    cropArea.value.x = dragStart.value.cropX + dx
    cropArea.value.y = dragStart.value.cropY + dy
  }
  clampCrop()
}

function onMouseUp() {
  isDragging.value = false
  resizing.value = false
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', onMouseUp)
}

function onTouchStart(e: TouchEvent, isResize = false) {
  if (!e.touches[0]) return
  e.preventDefault()
  isDragging.value = true
  resizing.value = isResize
  dragStart.value = {
    x: e.touches[0].clientX,
    y: e.touches[0].clientY,
    cropX: cropArea.value.x,
    cropY: cropArea.value.y
  }
  window.addEventListener('touchmove', onTouchMove, { passive: false })
  window.addEventListener('touchend', onTouchEnd)
}

function onTouchMove(e: TouchEvent) {
  if (!isDragging.value || !e.touches[0]) return
  e.preventDefault()
  const dx = e.touches[0].clientX - dragStart.value.x
  const dy = e.touches[0].clientY - dragStart.value.y

  if (resizing.value) {
    const ar = numericRatio.value
    if (ar) {
      const delta = (dx + dy) / 2
      cropArea.value.width = cropArea.value.width + delta
      cropArea.value.height = cropArea.value.width / ar
    } else {
      cropArea.value.width = cropArea.value.width + dx
      cropArea.value.height = cropArea.value.height + dy
    }
    dragStart.value.x = e.touches[0].clientX
    dragStart.value.y = e.touches[0].clientY
  } else {
    cropArea.value.x = dragStart.value.cropX + dx
    cropArea.value.y = dragStart.value.cropY + dy
  }
  clampCrop()
}

function onTouchEnd() {
  isDragging.value = false
  resizing.value = false
  window.removeEventListener('touchmove', onTouchMove)
  window.removeEventListener('touchend', onTouchEnd)
}

onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', onMouseUp)
  window.removeEventListener('touchmove', onTouchMove)
  window.removeEventListener('touchend', onTouchEnd)
})

const MIN_OUTPUT_DIM = 400

function doCrop() {
  if (!imgRef.value) return
  const img = imgRef.value
  const bounds = getImageBounds()

  const scaleX = img.naturalWidth / bounds.w
  const scaleY = img.naturalHeight / bounds.h

  const sx = (cropArea.value.x - bounds.x) * scaleX
  const sy = (cropArea.value.y - bounds.y) * scaleY
  const sW = cropArea.value.width * scaleX
  const sH = cropArea.value.height * scaleY

  const outputScale = Math.max(1, MIN_OUTPUT_DIM / Math.min(sW, sH))
  const outW = sW * outputScale
  const outH = sH * outputScale

  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, sx, sy, sW, sH, 0, 0, outW, outH)

  const mimeMatch = props.src?.match(/^data:(image\/\w+);/)
  const sourceMime = mimeMatch?.[1] ?? 'image/jpeg'
  const outputMime = sourceMime === 'image/png' ? 'image/png' : 'image/jpeg'

  canvas.toBlob(
    (blob) => {
      if (blob) emit('confirm', blob)
    },
    outputMime,
    0.92
  )
}

watch(
  () => props.src,
  (val) => {
    if (val) {
      nextTick(() => {
        const img = imgRef.value
        if (img && img.complete) onImageLoad()
      })
    }
  }
)
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="max-w-lg p-0 gap-0 overflow-hidden">
      <VisuallyHidden>
        <DialogTitle>{{ t('uploader.crop.title') }}</DialogTitle>
        <DialogDescription>{{ t('uploader.crop.description') }}</DialogDescription>
      </VisuallyHidden>
      <div ref="containerRef" class="relative bg-black select-none" style="height: 400px">
        <img
          v-if="src"
          ref="imgRef"
          :src="src"
          class="max-w-full max-h-full object-contain absolute inset-0 m-auto"
          draggable="false"
          @load="onImageLoad"
        />

        <div class="absolute inset-0 pointer-events-none" style="z-index: 1">
          <div class="absolute bg-black/50" :style="{ top: 0, left: 0, right: 0, height: `${cropArea.y}px` }" />
          <div
            class="absolute bg-black/50"
            :style="{ top: `${cropArea.y + cropArea.height}px`, left: 0, right: 0, bottom: 0 }"
          />
          <div
            class="absolute bg-black/50"
            :style="{
              top: `${cropArea.y}px`,
              left: 0,
              width: `${cropArea.x}px`,
              height: `${cropArea.height}px`
            }"
          />
          <div
            class="absolute bg-black/50"
            :style="{
              top: `${cropArea.y}px`,
              left: `${cropArea.x + cropArea.width}px`,
              right: 0,
              height: `${cropArea.height}px`
            }"
          />
        </div>

        <div
          class="absolute border-2 border-white rounded-md cursor-move"
          style="z-index: 2"
          :style="{
            left: `${cropArea.x}px`,
            top: `${cropArea.y}px`,
            width: `${cropArea.width}px`,
            height: `${cropArea.height}px`
          }"
          @mousedown="onMouseDown($event, false)"
          @touchstart="onTouchStart($event, false)"
        >
          <div
            class="absolute -bottom-1.5 -right-1.5 size-3 bg-white rounded-full cursor-nwse-resize"
            @mousedown.stop="onMouseDown($event, true)"
            @touchstart.stop="onTouchStart($event, true)"
          />
        </div>
      </div>

      <div class="px-4 pt-3 text-center text-xs text-muted-foreground">
        {{ formatLabel }}
      </div>

      <DialogFooter class="px-4 pt-2 pb-3 flex items-center justify-center gap-2 sm:justify-center">
        <DialogClose as-child>
          <Button variant="outline" size="sm" @click="emit('cancel')">
            <Trash2 class="size-4" />
            {{ t('global.words.cancel') }}
          </Button>
        </DialogClose>
        <Button size="sm" @click="doCrop">
          <Crop class="size-4" />
          {{ t('global.words.confirm') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
